import { Link } from "react-router-dom";
import { IMAGES, COMPANY } from "@/lib/site";
import { Section, Overline, FadeIn, PageHero } from "@/components/Primitives";
import { Handshake, BarChart3, Globe2, Ship, FileCheck2, MessageCircle } from "lucide-react";

const steps = [
  {
    no: "01",
    title: "Understand the requirement",
    body: "We start with a detailed brief — origin preference, grade, volume, packaging, delivery window and pricing benchmark.",
  },
  {
    no: "02",
    title: "Source from trusted origins",
    body: "We tap our network of vetted shippers, processors and exporters to identify the right counter-party.",
  },
  {
    no: "03",
    title: "Match & negotiate terms",
    body: "We introduce both sides, support negotiation on price, payment terms and Incoterms.",
  },
  {
    no: "04",
    title: "Oversee execution",
    body: "From contract signing through shipment, documentation and arrival — we stay in the loop until cargo lands clean.",
  },
];

const services = [
  { icon: Handshake, t: "Buyer ↔ Shipper introductions", d: "Vetted counter-parties on both sides of the deal." },
  { icon: BarChart3, t: "Daily market intelligence", d: "Price flow, origin updates, and demand signals." },
  { icon: Globe2, t: "Multi-origin coverage", d: "India · Africa · Brazil · Canada · Australia · Russia." },
  { icon: Ship, t: "Shipment oversight", d: "Logistics tracking and shipping documentation review." },
  { icon: FileCheck2, t: "Contract assistance", d: "GAFTA-style trade documentation support." },
  { icon: MessageCircle, t: "Honest brokerage", d: "Transparent commission, no double-dipping." },
];

export default function Brokerage() {
  return (
    <main data-testid="brokerage-page">
      <PageHero
        overline="International Brokerage"
        title={
          <>
            Brokering the world's
            <br />
            <em className="not-italic text-[#2A4D3B]">pulses, one deal at a time.</em>
          </>
        }
        subtitle={`Through ${COMPANY.fzco}, we run an independent international brokerage house dedicated to the global pulses industry. Origin shippers meet destination buyers — through a desk that knows both sides of the market.`}
        image={IMAGES.shipping}
      />

      <Section testid="brokerage-services" className="bg-[#FDFBF7]">
        <div className="max-w-3xl mb-14">
          <Overline>What we broker</Overline>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.06] text-[#1A1A1A]">
            A specialist desk for pulses, built for global flows.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <FadeIn key={s.t} delay={i * 0.06}>
              <article className="h-full rounded-3xl bg-white border border-[#EAE7E0] p-8 hover:-translate-y-1 hover:shadow-xl transition-all">
                <s.icon className="h-7 w-7 text-[#2A4D3B]" />
                <h3 className="mt-6 font-serif-display text-xl text-[#1A1A1A]">{s.t}</h3>
                <p className="mt-3 text-sm text-[#4A4540] leading-relaxed">{s.d}</p>
              </article>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section testid="brokerage-process" className="bg-[#F6F4F0]">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Overline>How we work</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.06] text-[#1A1A1A]">
              A simple, transparent brokerage process.
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {steps.map((s, i) => (
              <FadeIn key={s.no} delay={i * 0.08}>
                <div className="rounded-3xl bg-white border border-[#EAE7E0] p-8 h-full hover:shadow-xl transition-all">
                  <p className="font-serif-display text-3xl text-[#A25F4B]">{s.no}</p>
                  <h3 className="mt-4 font-serif-display text-xl text-[#1A1A1A]">{s.title}</h3>
                  <p className="mt-3 text-sm text-[#4A4540] leading-relaxed">{s.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </Section>

      <Section testid="brokerage-cta-section" className="bg-[#FDFBF7]">
        <div className="rounded-3xl bg-[#1A1A1A] text-[#FDFBF7] p-12 sm:p-20 relative overflow-hidden">
          <img src={IMAGES.network} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="relative max-w-3xl">
            <Overline dark>Talk to the desk</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.05]">
              Looking to source or sell pulses globally?
            </h2>
            <p className="mt-6 text-[#C8C2B5] text-lg leading-relaxed">
              We respond to every serious enquiry within one business day, with origin options and
              indicative pricing.
            </p>
            <Link
              to="/contact"
              data-testid="brokerage-page-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#1A1A1A] px-7 py-4 text-sm font-semibold hover:bg-[#D8A48F] transition-colors"
            >
              Brief the brokerage desk →
            </Link>
          </div>
        </div>
      </Section>
    </main>
  );
}
